/**
 * Live Tracking Storage for slide snapshots
 * Stores slide images and PPTX data for change detection
 *
 * ⚠️ IMPORTANT: IndexedDB data persists across add-in restarts by design.
 * Slide snapshots and deck tracking state survive PowerPoint close/reopen.
 * To clear all tracking data on startup for testing, uncomment the
 * clearAllOnStartup() call in initialize().
 */
// Legacy database names to clean up on startup
const LEGACY_DB_NAMES = ['SlideKnowledgeGraph', 'SlideTrackerDB', 'SlideTrackerOfflineQueue'];
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
export class LiveTrackingStorage {
    constructor() {
        this.dbName = 'SlideTrackerLive';
        this.dbVersion = 2; // Incremented for schema change
        this.snapshotStoreName = 'snapshots';
        this.deckStateStoreName = 'deckStates';
        this.isInitialized = false;
        this.hasAttemptedSchemaRecovery = false;
        this.hasAttemptedIntegrityReset = false;
    }
    normalizeKeyPath(keyPath) {
        if (Array.isArray(keyPath)) {
            return keyPath;
        }
        if (typeof keyPath === 'string') {
            return [keyPath];
        }
        return [];
    }
    keyPathMatches(actual, expected) {
        const actualParts = this.normalizeKeyPath(actual);
        if (actualParts.length !== expected.length) {
            return false;
        }
        return expected.every((part, idx) => actualParts[idx] === part);
    }
    validateDatabaseSchema(db) {
        const storeNames = db.objectStoreNames;
        const hasSnapshots = storeNames.contains(this.snapshotStoreName);
        const hasDeckStates = storeNames.contains(this.deckStateStoreName);
        if (!hasSnapshots || !hasDeckStates) {
            return {
                isValid: false,
                reason: `missing stores (snapshots: ${hasSnapshots}, deckStates: ${hasDeckStates})`,
            };
        }
        try {
            const transaction = db.transaction([this.snapshotStoreName, this.deckStateStoreName], 'readonly');
            const snapshotStore = transaction.objectStore(this.snapshotStoreName);
            const deckStore = transaction.objectStore(this.deckStateStoreName);
            if (!this.keyPathMatches(snapshotStore.keyPath, ['documentId', 'slideId'])) {
                return {
                    isValid: false,
                    reason: `snapshots keyPath mismatch (${JSON.stringify(snapshotStore.keyPath)})`,
                };
            }
            if (deckStore.keyPath !== 'documentId') {
                return {
                    isValid: false,
                    reason: `deckStates keyPath mismatch (${JSON.stringify(deckStore.keyPath)})`,
                };
            }
            const requiredSnapshotIndexes = ['documentId', 'slideId', 'timestamp'];
            for (const indexName of requiredSnapshotIndexes) {
                if (!snapshotStore.indexNames.contains(indexName)) {
                    return {
                        isValid: false,
                        reason: `snapshots missing index "${indexName}"`,
                    };
                }
            }
            if (!deckStore.indexNames.contains('isTracking')) {
                return {
                    isValid: false,
                    reason: 'deckStates missing index "isTracking"',
                };
            }
        }
        catch (error) {
            return {
                isValid: false,
                reason: `schema inspection failed: ${error instanceof Error ? error.message : String(error)}`,
            };
        }
        return { isValid: true, reason: 'ok' };
    }
    isNonEmptyString(value) {
        return typeof value === 'string' && value.trim().length > 0;
    }
    isValidIndexingStatus(value) {
        return value === 'pending' || value === 'in_flight' || value === 'retrying' || value === 'success' || value === 'failed';
    }
    toFiniteNumber(value) {
        const parsed = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    async deleteDatabaseWithReason(reason) {
        await new Promise((resolve, reject) => {
            const deleteRequest = indexedDB.deleteDatabase(this.dbName);
            deleteRequest.onsuccess = () => {
                console.log(`[Storage] ✅ Deleted database (${reason})`);
                resolve();
            };
            deleteRequest.onerror = () => {
                console.error(`[Storage] ❌ Failed to delete database (${reason})`);
                reject(new Error(`Failed deleting database (${reason})`));
            };
            deleteRequest.onblocked = () => {
                console.error(`[Storage] ❌ Database deletion blocked (${reason})`);
                reject(new Error(`Database deletion blocked (${reason})`));
            };
        });
    }
    sanitizeDeckState(raw) {
        if (!raw || typeof raw !== 'object') {
            return { sanitized: null, changed: false };
        }
        const source = raw;
        if (!this.isNonEmptyString(source.documentId)) {
            return { sanitized: null, changed: true };
        }
        const now = Date.now();
        const lastStructureCheck = this.toFiniteNumber(source.lastStructureCheck);
        const lastSelectionTime = this.toFiniteNumber(source.lastSelectionTime);
        const lastActiveAt = this.toFiniteNumber(source.lastActiveAt);
        const inFlightCount = this.toFiniteNumber(source.inFlightCount);
        const retryingCount = this.toFiniteNumber(source.retryingCount);
        const sanitized = {
            documentId: source.documentId,
            deckName: this.isNonEmptyString(source.deckName) ? source.deckName : 'Untitled',
            slideIds: Array.isArray(source.slideIds)
                ? source.slideIds.filter((id) => this.isNonEmptyString(id))
                : [],
            lastStructureCheck: lastStructureCheck ?? now,
            isTracking: typeof source.isTracking === 'boolean' ? source.isTracking : true,
            lastSelectionTime: lastSelectionTime ?? now,
        };
        if (this.isNonEmptyString(source.lastSelectedSlide)) {
            sanitized.lastSelectedSlide = source.lastSelectedSlide;
        }
        if (lastActiveAt !== null) {
            sanitized.lastActiveAt = lastActiveAt;
        }
        else {
            sanitized.lastActiveAt = now;
        }
        if (typeof source.indexingEnabled === 'boolean') {
            sanitized.indexingEnabled = source.indexingEnabled;
        }
        if (typeof source.indexingDeferred === 'boolean') {
            sanitized.indexingDeferred = source.indexingDeferred;
        }
        if (inFlightCount !== null) {
            sanitized.inFlightCount = inFlightCount;
        }
        if (retryingCount !== null) {
            sanitized.retryingCount = retryingCount;
        }
        const changed = JSON.stringify(source) !== JSON.stringify(sanitized);
        return { sanitized, changed };
    }
    sanitizeSnapshot(raw) {
        if (!raw || typeof raw !== 'object') {
            return { sanitized: null, changed: false };
        }
        const source = raw;
        if (!this.isNonEmptyString(source.documentId) || !this.isNonEmptyString(source.slideId)) {
            return { sanitized: null, changed: true };
        }
        const now = Date.now();
        const slideIndex = this.toFiniteNumber(source.slideIndex);
        const timestamp = this.toFiniteNumber(source.timestamp);
        const touchCount = this.toFiniteNumber(source.touchCount);
        const prevTimestamp = this.toFiniteNumber(source.prevTimestamp);
        const lastProcessed = this.toFiniteNumber(source.lastProcessed);
        const descriptionUpdatedAt = this.toFiniteNumber(source.descriptionUpdatedAt);
        const sanitized = {
            documentId: source.documentId,
            slideId: source.slideId,
            slideIndex: slideIndex ?? 0,
            imageBase64: typeof source.imageBase64 === 'string' ? source.imageBase64 : '',
            pptxBase64: typeof source.pptxBase64 === 'string' ? source.pptxBase64 : '',
            timestamp: timestamp ?? now,
            touchCount: touchCount !== null && touchCount >= 0 ? touchCount : 0,
        };
        if (typeof source.prevImageBase64 === 'string')
            sanitized.prevImageBase64 = source.prevImageBase64;
        if (typeof source.prevPptxBase64 === 'string')
            sanitized.prevPptxBase64 = source.prevPptxBase64;
        if (prevTimestamp !== null)
            sanitized.prevTimestamp = prevTimestamp;
        if (typeof source.layoutId === 'string')
            sanitized.layoutId = source.layoutId;
        if (typeof source.layoutName === 'string')
            sanitized.layoutName = source.layoutName;
        if (typeof source.masterTemplateId === 'string')
            sanitized.masterTemplateId = source.masterTemplateId;
        if (typeof source.masterTemplateName === 'string')
            sanitized.masterTemplateName = source.masterTemplateName;
        if (lastProcessed !== null)
            sanitized.lastProcessed = lastProcessed;
        if (typeof source.shortDescription === 'string')
            sanitized.shortDescription = source.shortDescription;
        if (descriptionUpdatedAt !== null)
            sanitized.descriptionUpdatedAt = descriptionUpdatedAt;
        if (source.quickMetadata && typeof source.quickMetadata === 'object')
            sanitized.quickMetadata = source.quickMetadata;
        if (this.isValidIndexingStatus(source.indexingStatus)) {
            sanitized.indexingStatus = source.indexingStatus;
        }
        else if (source.indexingStatus !== undefined) {
            sanitized.indexingStatus = 'pending';
        }
        const changed = JSON.stringify(source) !== JSON.stringify(sanitized);
        return { sanitized, changed };
    }
    async scrubDatabaseIntegrity() {
        if (!this.db) {
            return {
                severe: true,
                reason: 'database handle unavailable',
                scanned: 0,
                updated: 0,
                deleted: 0,
            };
        }
        return new Promise((resolve) => {
            const transaction = this.db.transaction([this.snapshotStoreName, this.deckStateStoreName], 'readwrite');
            const snapshotStore = transaction.objectStore(this.snapshotStoreName);
            const deckStore = transaction.objectStore(this.deckStateStoreName);
            let scanned = 0;
            let updated = 0;
            let deleted = 0;
            let fatalReason = null;
            const markFatal = (reason, error) => {
                if (!fatalReason) {
                    fatalReason = error instanceof Error ? `${reason}: ${error.message}` : reason;
                }
            };
            const scrubDeckCursor = deckStore.openCursor();
            scrubDeckCursor.onerror = () => {
                markFatal('deckStates cursor failed', scrubDeckCursor.error);
            };
            scrubDeckCursor.onsuccess = () => {
                const cursor = scrubDeckCursor.result;
                if (!cursor) {
                    return;
                }
                scanned++;
                const { sanitized, changed } = this.sanitizeDeckState(cursor.value);
                if (!sanitized) {
                    cursor.delete();
                    deleted++;
                    cursor.continue();
                    return;
                }
                if (changed) {
                    cursor.update(sanitized);
                    updated++;
                }
                cursor.continue();
            };
            const scrubSnapshotCursor = snapshotStore.openCursor();
            scrubSnapshotCursor.onerror = () => {
                markFatal('snapshots cursor failed', scrubSnapshotCursor.error);
            };
            scrubSnapshotCursor.onsuccess = () => {
                const cursor = scrubSnapshotCursor.result;
                if (!cursor) {
                    return;
                }
                scanned++;
                const { sanitized, changed } = this.sanitizeSnapshot(cursor.value);
                if (!sanitized) {
                    cursor.delete();
                    deleted++;
                    cursor.continue();
                    return;
                }
                if (changed) {
                    cursor.update(sanitized);
                    updated++;
                }
                cursor.continue();
            };
            transaction.oncomplete = () => {
                const severeByVolume = scanned > 0 && deleted > 0 && deleted >= scanned;
                resolve({
                    severe: !!fatalReason || severeByVolume,
                    reason: fatalReason || (severeByVolume ? 'all records invalidated during scrub' : 'ok'),
                    scanned,
                    updated,
                    deleted,
                });
            };
            transaction.onerror = () => {
                resolve({
                    severe: true,
                    reason: transaction.error?.message || 'integrity scrub transaction failed',
                    scanned,
                    updated,
                    deleted,
                });
            };
            transaction.onabort = () => {
                resolve({
                    severe: true,
                    reason: transaction.error?.message || 'integrity scrub transaction aborted',
                    scanned,
                    updated,
                    deleted,
                });
            };
        });
    }
    async recoverFromSchemaMismatch(reason) {
        console.warn(`[Storage] ⚠️ Database schema mismatch: ${reason}`);
        if (this.hasAttemptedSchemaRecovery) {
            throw new Error(`[Storage] Repeated schema recovery requested (${reason})`);
        }
        this.hasAttemptedSchemaRecovery = true;
        try {
            console.log('[Storage] Deleting corrupted database and recreating...');
            if (this.db) {
                this.db.close();
                this.db = undefined;
            }
            this.isInitialized = false;
            await this.deleteDatabaseWithReason('schema mismatch recovery');
            await this.initializeFresh();
            if (!this.db) {
                throw new Error('fresh database handle missing after recovery');
            }
            const validation = this.validateDatabaseSchema(this.db);
            if (!validation.isValid) {
                console.warn(`[Storage] Fresh DB schema still invalid (${validation.reason}); forcing hard reset once`);
                await this.deleteDatabaseWithReason('post-recovery schema validation failure');
                await this.initializeFresh();
            }
            if (!this.db) {
                throw new Error('database missing after hard reset');
            }
            const finalValidation = this.validateDatabaseSchema(this.db);
            if (!finalValidation.isValid) {
                throw new Error(`schema still invalid after hard reset: ${finalValidation.reason}`);
            }
        }
        finally {
            this.hasAttemptedSchemaRecovery = false;
        }
    }
    /**
     * Delete legacy IndexedDB databases that are no longer used
     */
    async cleanupLegacyDatabases() {
        for (const dbName of LEGACY_DB_NAMES) {
            try {
                await new Promise((resolve) => {
                    const request = indexedDB.deleteDatabase(dbName);
                    request.onsuccess = () => {
                        console.log(`[Storage] ✅ Deleted legacy database: ${dbName}`);
                        resolve();
                    };
                    request.onerror = () => {
                        console.warn(`[Storage] Could not delete legacy database ${dbName}:`, request.error);
                        resolve(); // Don't fail initialization for cleanup errors
                    };
                    request.onblocked = () => {
                        console.warn(`[Storage] Legacy database ${dbName} is blocked - may be open elsewhere`);
                        resolve();
                    };
                });
            }
            catch (e) {
                console.warn(`[Storage] Error deleting legacy database ${dbName}:`, e);
            }
        }
    }
    /**
     * Clear all tracking data on startup (for testing/development)
     * Uncomment the call to this in initialize() to force fresh tracking each session.
     */
    async clearAllOnStartup() {
        console.log('[Storage] ⚠️ clearAllOnStartup() called - clearing all tracking data');
        try {
            // Delete the entire database to ensure clean slate
            await new Promise((resolve) => {
                const request = indexedDB.deleteDatabase(this.dbName);
                request.onsuccess = () => {
                    console.log('[Storage] ✅ Database deleted for fresh start');
                    resolve();
                };
                request.onerror = () => {
                    console.warn('[Storage] Could not delete database:', request.error);
                    resolve(); // Continue anyway
                };
                request.onblocked = () => {
                    console.warn('[Storage] Database deletion blocked');
                    resolve();
                };
            });
            this.isInitialized = false;
            this.db = undefined;
        }
        catch (e) {
            console.warn('[Storage] Error in clearAllOnStartup:', e);
        }
    }
    async initialize() {
        if (this.isInitialized)
            return;
        // ⚠️ UNCOMMENT TO CLEAR ALL TRACKING DATA ON EVERY STARTUP (for testing):
        // await this.clearAllOnStartup();
        // Clean up legacy databases first
        await this.cleanupLegacyDatabases();
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);
            request.onerror = () => {
                console.error('Failed to open live tracking database');
                reject(request.error);
            };
            request.onsuccess = () => {
                this.db = request.result;
                const schemaValidation = this.validateDatabaseSchema(this.db);
                if (!schemaValidation.isValid) {
                    this.recoverFromSchemaMismatch(schemaValidation.reason).then(resolve).catch(reject);
                    return;
                }
                this.scrubDatabaseIntegrity()
                    .then((scrubResult) => {
                    if (scrubResult.updated > 0 || scrubResult.deleted > 0) {
                        console.log('[Storage] Integrity scrub updated records:', scrubResult);
                    }
                    if (scrubResult.severe) {
                        if (this.hasAttemptedIntegrityReset) {
                            reject(new Error(`[Storage] Severe integrity issues persisted after reset: ${scrubResult.reason}`));
                            return;
                        }
                        this.hasAttemptedIntegrityReset = true;
                        this.recoverFromSchemaMismatch(`integrity scrub severe: ${scrubResult.reason}`)
                            .then(resolve)
                            .catch(reject);
                        return;
                    }
                    this.isInitialized = true;
                    console.log('Live tracking database initialized');
                    resolve();
                })
                    .catch((scrubError) => {
                    reject(scrubError);
                });
            };
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                const oldVersion = event.oldVersion;
                const transaction = event.target.transaction;
                console.log(`[LiveTrackingStorage] Database upgrade: v${oldVersion} → v${this.dbVersion}`);
                // Create snapshots store
                if (!db.objectStoreNames.contains(this.snapshotStoreName)) {
                    const snapshotStore = db.createObjectStore(this.snapshotStoreName, {
                        keyPath: ['documentId', 'slideId']
                    });
                    // Indexes for efficient querying
                    snapshotStore.createIndex('documentId', 'documentId', { unique: false });
                    snapshotStore.createIndex('slideId', 'slideId', { unique: false });
                    snapshotStore.createIndex('timestamp', 'timestamp', { unique: false });
                    snapshotStore.createIndex('imageHash', 'imageHash', { unique: false });
                }
                // Create deck state store
                if (!db.objectStoreNames.contains(this.deckStateStoreName)) {
                    const deckStore = db.createObjectStore(this.deckStateStoreName, {
                        keyPath: 'documentId'
                    });
                    deckStore.createIndex('isTracking', 'isTracking', { unique: false });
                }
                // Migration from v1 to v2: Add quickMetadata field to existing snapshots
                if (oldVersion < 2 && db.objectStoreNames.contains(this.snapshotStoreName)) {
                    console.log(`[LiveTrackingStorage] Migrating existing snapshots to add quickMetadata field...`);
                    const store = transaction.objectStore(this.snapshotStoreName);
                    const getAllRequest = store.getAll();
                    getAllRequest.onsuccess = () => {
                        const snapshots = getAllRequest.result;
                        console.log(`[LiveTrackingStorage] Migrating ${snapshots.length} snapshots...`);
                        let migratedCount = 0;
                        for (const snapshot of snapshots) {
                            // Add quickMetadata field if it doesn't exist (set to undefined for lazy population)
                            if (!snapshot.quickMetadata) {
                                snapshot.quickMetadata = undefined;
                                store.put(snapshot);
                                migratedCount++;
                            }
                        }
                        console.log(`[LiveTrackingStorage] ✅ Migration complete: ${migratedCount}/${snapshots.length} snapshots updated`);
                    };
                    getAllRequest.onerror = () => {
                        console.error(`[LiveTrackingStorage] ❌ Migration failed:`, getAllRequest.error);
                    };
                }
            };
        });
    }
    /**
     * Initialize with a fresh database (used for recovery from corruption)
     */
    async initializeFresh() {
        return new Promise((resolve, reject) => {
            // Use same version - the database was deleted so upgrade will run
            const request = indexedDB.open(this.dbName, this.dbVersion);
            request.onerror = () => {
                console.error('[Storage] Failed to create fresh database');
                reject(request.error);
            };
            request.onsuccess = () => {
                this.db = request.result;
                this.isInitialized = true;
                console.log('[Storage] ✅ Fresh database created successfully');
                resolve();
            };
            request.onupgradeneeded = (event) => {
                console.log('[Storage] Creating fresh object stores...');
                const db = event.target.result;
                // Create snapshots store
                const snapshotStore = db.createObjectStore(this.snapshotStoreName, {
                    keyPath: ['documentId', 'slideId']
                });
                snapshotStore.createIndex('documentId', 'documentId', { unique: false });
                snapshotStore.createIndex('slideId', 'slideId', { unique: false });
                snapshotStore.createIndex('timestamp', 'timestamp', { unique: false });
                snapshotStore.createIndex('imageHash', 'imageHash', { unique: false });
                // Create deck state store
                const deckStore = db.createObjectStore(this.deckStateStoreName, {
                    keyPath: 'documentId'
                });
                deckStore.createIndex('isTracking', 'isTracking', { unique: false });
                console.log('[Storage] ✅ Object stores created');
            };
        });
    }
    async ensureInitialized() {
        if (!this.isInitialized) {
            await this.initialize();
        }
    }
    getDeckLastActiveTimestamp(state) {
        return state.lastActiveAt || state.lastSelectionTime || state.lastStructureCheck || 0;
    }
    /**
     * Save or update a slide snapshot
     */
    async saveSnapshot(snapshot) {
        await this.ensureInitialized();
        const documentId = snapshot.documentId;
        if (!documentId) {
            throw new Error("[Storage] saveSnapshot requires documentId.");
        }
        const snapshotToSave = {
            ...snapshot,
            documentId,
        };
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName], 'readwrite');
            const store = transaction.objectStore(this.snapshotStoreName);
            const request = store.put(snapshotToSave);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
    /**
     * Get a slide snapshot
     */
    async getSnapshot(documentId, slideId) {
        await this.ensureInitialized();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName], 'readonly');
            const store = transaction.objectStore(this.snapshotStoreName);
            const request = store.get([documentId, slideId]);
            request.onsuccess = () => {
                resolve(request.result);
            };
            request.onerror = () => reject(request.error);
        });
    }
    /**
     * Get all snapshots for a deck
     */
    async getDeckSnapshots(documentId) {
        await this.ensureInitialized();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName], 'readonly');
            const store = transaction.objectStore(this.snapshotStoreName);
            const index = store.index('documentId');
            const request = index.getAll(documentId);
            request.onsuccess = () => {
                resolve((request.result || []));
            };
            request.onerror = () => reject(request.error);
        });
    }
    /**
     * Batch save snapshots (for initial full deck processing)
     */
    async batchSaveSnapshots(snapshots) {
        await this.ensureInitialized();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName], 'readwrite');
            const store = transaction.objectStore(this.snapshotStoreName);
            let processed = 0;
            snapshots.forEach(snapshot => {
                const documentId = snapshot.documentId;
                if (!documentId) {
                    reject(new Error("[Storage] batchSaveSnapshots requires documentId for all snapshots."));
                    return;
                }
                const snapshotToSave = {
                    ...snapshot,
                    documentId,
                };
                const request = store.put(snapshotToSave);
                request.onsuccess = () => {
                    processed++;
                    if (processed === snapshots.length)
                        resolve();
                };
                request.onerror = () => reject(request.error);
            });
            if (snapshots.length === 0)
                resolve();
        });
    }
    /**
     * Delete snapshot
     */
    async deleteSnapshot(documentId, slideId) {
        await this.ensureInitialized();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName], 'readwrite');
            const store = transaction.objectStore(this.snapshotStoreName);
            const request = store.delete([documentId, slideId]);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
    /**
     * Update the short description for a slide snapshot.
     * Only updates if the description is different from what's stored.
     * @returns true if description was updated, false if unchanged or not found
     */
    async updateSlideDescription(documentId, slideId, shortDescription) {
        await this.ensureInitialized();
        const existing = await this.getSnapshot(documentId, slideId);
        if (!existing) {
            console.warn(`[Storage] Cannot update description - snapshot not found: ${documentId}/${slideId}`);
            return false;
        }
        // Only update if different
        if (existing.shortDescription === shortDescription) {
            return false;
        }
        // Update the snapshot with new description
        const updated = {
            ...existing,
            shortDescription,
            descriptionUpdatedAt: Date.now(),
            indexingStatus: this.deriveIndexingStatusFromDescription(shortDescription, existing.indexingStatus)
        };
        await this.saveSnapshot(updated);
        console.log(`[Storage] Updated description for ${slideId}: "${shortDescription.substring(0, 50)}..."`);
        return true;
    }
    /**
     * Batch update descriptions for multiple slides.
     * @returns number of slides that were actually updated
     */
    async batchUpdateDescriptions(documentId, descriptions) {
        let updatedCount = 0;
        for (const { slideId, shortDescription } of descriptions) {
            const updated = await this.updateSlideDescription(documentId, slideId, shortDescription);
            if (updated)
                updatedCount++;
        }
        if (updatedCount > 0) {
            console.log(`[Storage] Batch updated ${updatedCount}/${descriptions.length} descriptions`);
        }
        return updatedCount;
    }
    /**
     * Update indexing status for a specific slide snapshot.
     * @returns true if status changed, false if unchanged or snapshot missing
     */
    async updateSlideIndexingStatus(documentId, slideId, indexingStatus) {
        await this.ensureInitialized();
        const existing = await this.getSnapshot(documentId, slideId);
        if (!existing) {
            return false;
        }
        if (existing.indexingStatus === indexingStatus) {
            return false;
        }
        await this.saveSnapshot({
            ...existing,
            indexingStatus,
        });
        return true;
    }
    deriveIndexingStatusFromDescription(shortDescription, current) {
        const normalized = (shortDescription || '').trim().toLowerCase();
        if (normalized === 'processing failed') {
            return 'failed';
        }
        if (normalized.length > 0) {
            return 'success';
        }
        return current || 'pending';
    }
    /**
     * Save deck tracking state
     */
    async saveDeckState(state) {
        await this.ensureInitialized();
        const documentId = state.documentId;
        if (!documentId) {
            throw new Error("[Storage] saveDeckState requires documentId.");
        }
        const stateToSave = {
            ...state,
            documentId,
            lastActiveAt: Date.now(),
        };
        console.log(`[Storage] Saving deck state: ${stateToSave.documentId}, isTracking=${stateToSave.isTracking}, slides=${stateToSave.slideIds?.length || 0}`);
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.deckStateStoreName], 'readwrite');
            const store = transaction.objectStore(this.deckStateStoreName);
            const request = store.put(stateToSave);
            request.onsuccess = () => {
                console.log(`[Storage] ✅ Deck state saved: ${stateToSave.documentId}`);
                resolve();
            };
            request.onerror = () => {
                console.error(`[Storage] ❌ Error saving deck state:`, request.error);
                reject(request.error);
            };
        });
    }
    /**
     * Get deck tracking state
     */
    async getDeckState(documentId) {
        await this.ensureInitialized();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.deckStateStoreName], 'readonly');
            const store = transaction.objectStore(this.deckStateStoreName);
            const request = store.get(documentId);
            request.onsuccess = () => {
                const state = request.result;
                if (state) {
                    console.log(`[Storage] Found deck state: ${documentId}, isTracking=${state.isTracking}, slides=${state.slideIds?.length || 0}`);
                }
                else {
                    console.log(`[Storage] No deck state found for: ${documentId}`);
                }
                resolve(state);
            };
            request.onerror = () => {
                console.error(`[Storage] Error getting deck state:`, request.error);
                reject(request.error);
            };
        });
    }
    /**
     * Get deck-level indexing override for a deck.
     * Returns null when no explicit override is set.
     */
    async getDeckIndexingOverride(documentId) {
        const state = await this.getDeckState(documentId);
        if (!state || typeof state.indexingEnabled !== 'boolean') {
            return null;
        }
        return state.indexingEnabled;
    }
    /**
     * Set deck-level indexing override for a deck.
     * Returns false when deck state does not exist.
     */
    async setDeckIndexingOverride(documentId, enabled) {
        const state = await this.getDeckState(documentId);
        if (!state) {
            console.warn(`[Storage] Cannot set deck indexing override - deck state not found: ${documentId}`);
            return false;
        }
        if (state.indexingEnabled === enabled) {
            return true;
        }
        await this.saveDeckState({
            ...state,
            indexingEnabled: enabled,
        });
        console.log(`[Storage] Updated deck indexing override: ${documentId} => ${enabled ? 'ON' : 'OFF'}`);
        return true;
    }
    /**
     * Get all tracked decks
     */
    async getAllTrackedDecks() {
        await this.ensureInitialized();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.deckStateStoreName], 'readonly');
            const store = transaction.objectStore(this.deckStateStoreName);
            // Get all decks (not just those with isTracking: true)
            const request = store.getAll();
            request.onsuccess = () => {
                const decks = request.result || [];
                console.log(`[Storage] getAllTrackedDecks: Found ${decks.length} decks`);
                decks.forEach(deck => {
                    console.log(`[Storage]   - Deck ${deck.documentId}: isTracking=${deck.isTracking}, slides=${deck.slideIds?.length || 0}`);
                });
                resolve(decks);
            };
            request.onerror = () => {
                console.error('[Storage] Error getting tracked decks:', request.error);
                reject(request.error);
            };
        });
    }
    /**
     * Clear all snapshots for a deck
     */
    async clearDeckSnapshots(documentId) {
        const snapshots = await this.getDeckSnapshots(documentId);
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName], 'readwrite');
            const store = transaction.objectStore(this.snapshotStoreName);
            snapshots.forEach(snapshot => {
                store.delete([documentId, snapshot.slideId]);
            });
            transaction.oncomplete = () => resolve();
            transaction.onerror = () => reject(transaction.error);
        });
    }
    /**
     * Delete a deck state record.
     */
    async deleteDeckState(documentId) {
        await this.ensureInitialized();
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.deckStateStoreName], 'readwrite');
            const store = transaction.objectStore(this.deckStateStoreName);
            const request = store.delete(documentId);
            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }
    /**
     * Delete all local tracking data for a deck in one transaction.
     * Returns how many snapshots were removed.
     */
    async deleteDeckTrackingData(documentId) {
        await this.ensureInitialized();
        const snapshots = await this.getDeckSnapshots(documentId);
        const snapshotKeys = snapshots.map((snapshot) => [documentId, snapshot.slideId]);
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName, this.deckStateStoreName], 'readwrite');
            const snapshotStore = transaction.objectStore(this.snapshotStoreName);
            const deckStateStore = transaction.objectStore(this.deckStateStoreName);
            for (const key of snapshotKeys) {
                snapshotStore.delete(key);
            }
            deckStateStore.delete(documentId);
            transaction.oncomplete = () => {
                resolve({ deletedSnapshots: snapshotKeys.length });
            };
            transaction.onerror = () => {
                reject(transaction.error);
            };
        });
    }
    /**
     * Cleanup deck tracking data older than the retention window.
     * Decks in excludeDocumentIds are always preserved.
     */
    async cleanupOldDecks(retentionDays, options) {
        await this.ensureInitialized();
        const safeRetentionDays = Number.isFinite(retentionDays)
            ? Math.max(1, Math.floor(retentionDays))
            : 7;
        const cutoffTimestamp = Date.now() - safeRetentionDays * MILLISECONDS_PER_DAY;
        const excludedDocumentIds = new Set(options?.excludeDocumentIds || []);
        const trackedDecks = await this.getAllTrackedDecks();
        let deletedDecks = 0;
        let deletedSnapshots = 0;
        let keptDecks = 0;
        console.log(`[Storage] Cleaning deck tracking data older than ${safeRetentionDays} days (cutoff=${new Date(cutoffTimestamp).toISOString()})`);
        for (const deckState of trackedDecks) {
            if (excludedDocumentIds.has(deckState.documentId)) {
                keptDecks++;
                console.log(`[Storage] Keeping excluded deck: ${deckState.documentId}`);
                continue;
            }
            const lastActiveTimestamp = this.getDeckLastActiveTimestamp(deckState);
            if (lastActiveTimestamp >= cutoffTimestamp) {
                keptDecks++;
                continue;
            }
            try {
                const result = await this.deleteDeckTrackingData(deckState.documentId);
                deletedDecks++;
                deletedSnapshots += result.deletedSnapshots;
                console.log(`[Storage] Deleted stale deck tracking data: ${deckState.documentId} (snapshots=${result.deletedSnapshots})`);
            }
            catch (error) {
                console.warn(`[Storage] Failed to delete stale deck ${deckState.documentId}:`, error);
            }
        }
        console.log('[Storage] Deck cleanup complete:', {
            retentionDays: safeRetentionDays,
            cutoffTimestamp,
            deletedDecks,
            deletedSnapshots,
            keptDecks,
            totalDecks: trackedDecks.length,
        });
        return { deletedDecks, deletedSnapshots, keptDecks };
    }
    /**
     * Clear all data
     */
    async clearAll(clearOtherDatabases = true) {
        await this.ensureInitialized();
        console.log('[Storage] Starting clearAll()...');
        console.log(`[Storage] Database: ${this.dbName}, DB instance: ${this.db ? 'exists' : 'null'}`);
        return new Promise((resolve, reject) => {
            if (!this.db) {
                const error = new Error('Database not initialized');
                console.error('[Storage] ❌', error);
                reject(error);
                return;
            }
            try {
                const transaction = this.db.transaction([this.snapshotStoreName, this.deckStateStoreName], 'readwrite');
                console.log(`[Storage] Clearing object store: ${this.snapshotStoreName}`);
                const snapshotStore = transaction.objectStore(this.snapshotStoreName);
                const snapshotClearRequest = snapshotStore.clear();
                snapshotClearRequest.onsuccess = () => {
                    console.log(`[Storage] ✅ Cleared ${this.snapshotStoreName}`);
                };
                snapshotClearRequest.onerror = () => {
                    console.error(`[Storage] ❌ Error clearing ${this.snapshotStoreName}:`, snapshotClearRequest.error);
                };
                console.log(`[Storage] Clearing object store: ${this.deckStateStoreName}`);
                const deckStore = transaction.objectStore(this.deckStateStoreName);
                const deckClearRequest = deckStore.clear();
                deckClearRequest.onsuccess = () => {
                    console.log(`[Storage] ✅ Cleared ${this.deckStateStoreName}`);
                };
                deckClearRequest.onerror = () => {
                    console.error(`[Storage] ❌ Error clearing ${this.deckStateStoreName}:`, deckClearRequest.error);
                };
                transaction.oncomplete = async () => {
                    console.log('[Storage] ✅ Transaction complete - all data cleared');
                    // Optionally clear other databases for full cleanup
                    if (clearOtherDatabases) {
                        console.log('[Storage] Clearing other databases...');
                        const dbNames = ['SlideKnowledgeGraph', 'SlideTrackerDB', 'SlideTrackerOfflineQueue'];
                        for (const dbName of dbNames) {
                            try {
                                await indexedDB.deleteDatabase(dbName);
                                console.log(`[Storage] ✅ Deleted database: ${dbName}`);
                            }
                            catch (e) {
                                console.warn(`[Storage] Could not delete database ${dbName}:`, e);
                            }
                        }
                    }
                    resolve();
                };
                transaction.onerror = () => {
                    console.error('[Storage] ❌ Transaction error:', transaction.error);
                    reject(transaction.error);
                };
            }
            catch (error) {
                console.error('[Storage] ❌ Exception in clearAll:', error);
                reject(error);
            }
        });
    }
    /**
     * Get storage stats
     */
    async getStats() {
        await this.ensureInitialized();
        const snapshotCount = await new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.snapshotStoreName], 'readonly');
            const store = transaction.objectStore(this.snapshotStoreName);
            const request = store.count();
            request.onsuccess = () => {
                console.log(`[Storage] Snapshot count: ${request.result}`);
                resolve(request.result);
            };
            request.onerror = () => reject(request.error);
        });
        const deckCount = await new Promise((resolve, reject) => {
            const transaction = this.db.transaction([this.deckStateStoreName], 'readonly');
            const store = transaction.objectStore(this.deckStateStoreName);
            const request = store.count();
            request.onsuccess = () => {
                console.log(`[Storage] Deck count: ${request.result}`);
                resolve(request.result);
            };
            request.onerror = () => reject(request.error);
        });
        // Rough estimate: assume avg 100KB per snapshot (base64 image + PPTX)
        const estimatedSize = snapshotCount * 100 * 1024;
        return { snapshotCount, deckCount, estimatedSize };
    }
    close() {
        if (this.db) {
            this.db.close();
            this.db = undefined;
            this.isInitialized = false;
        }
    }
}
/**
 * Fast hash function for image comparison
 * Simple FNV-1a hash implementation
 */
export function fastHash(str) {
    let hash = 2166136261; // FNV offset basis
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(36);
}
/**
 * Sample a base64 string for faster comparison
 * Takes every Nth character to create a smaller fingerprint
 */
export function sampleBase64(base64, sampleRate = 100) {
    let sample = '';
    for (let i = 0; i < base64.length; i += sampleRate) {
        sample += base64[i];
    }
    return sample;
}
